import {
  ChangeEvent,
  useEffect,
  useState,
  KeyboardEvent,
  useMemo
} from 'react';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Link } from 'react-router-dom';
import SearchBox from 'components/common/SearchBox';
import {
  AccountsTable,
  accountsTablecolumns
} from 'components/tables/AccountsTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { fetchAccounts, Account } from 'services/account.service';
import FeatherIcon from 'feather-icons-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const AccountList = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showNoRolesOnly, setShowNoRolesOnly] = useState(false);

  const refreshAccounts = async () => {
    try {
      const accountsData = await fetchAccounts();
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  useEffect(() => {
    refreshAccounts();
  }, []);

  useEffect(() => {
    if (showNoRolesOnly && getSamlUsersWithoutRoles() === 0) {
      setShowNoRolesOnly(false);
    }
  }, [accounts]);

  const getSamlUsersWithoutRoles = () => {
    return accounts.filter(
      account =>
        account.auth_type?.startsWith('saml') &&
        (!account.roles || account.roles.trim() === '')
    ).length;
  };

  // Filtramos los datos según showNoRolesOnly
  const filteredAccounts = useMemo(
    () =>
      showNoRolesOnly
        ? accounts.filter(
            account =>
              account.auth_type?.startsWith('saml') &&
              (!account.roles || account.roles.trim() === '')
          )
        : accounts,
    [accounts, showNoRolesOnly]
  );

  const table = useAdvanceTable({
    data: filteredAccounts,
    columns: accountsTablecolumns(refreshAccounts),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const NoRolesBadge = () => {
    const count = getSamlUsersWithoutRoles();
    if (count === 0) return null;

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="no-roles-tooltip">
            Click to
            {showNoRolesOnly
              ? ' show all users'
              : ' filter Single Sign On users without roles'}
          </Tooltip>
        }
      >
        <div
          className="cursor-pointer"
          onClick={() => setShowNoRolesOnly(!showNoRolesOnly)}
        >
          <span
            className={`badge d-flex align-items-center gap-2 p-2 px-4 ${
              showNoRolesOnly ? 'bg-warning-darker' : 'bg-warning '
            }`}
          >
            <FeatherIcon icon="alert-triangle" size={20} />
            {count} SSO user{count > 1 ? 's' : ''} without roles
          </span>
        </div>
      </OverlayTrigger>
    );
  };

  return (
    <div>
      <div className="mb-9">
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search users"
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyPress}
              />
              <NoRolesBadge />
              <div className="ms-auto order-2">
                <Link to="/user-management/register-user">
                  <Button variant="primary" className="p-2">
                    <FontAwesomeIcon icon={faUserPlus} className="me-2 fs-8" />
                    Create New Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <AccountsTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default AccountList;
